import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { type ComponentPropsWithRef } from 'react';

import { useDialog } from '@repo/ui/Dialog/DialogRoot';
import { getPolymorphicProps } from '@repo/ui/utils/polymorphic';

import s from './DialogTitle.module.css';

const DEFAULT_ELEMENT = 'h2' as const;

type AllowedDialogTitleElements = 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

/**
 * Props specific to the DialogTitle component
 */
type DialogTitleOwnProps<
  T extends AllowedDialogTitleElements = typeof DEFAULT_ELEMENT,
> = {
  as?: T;
  asChild?: boolean;
  position?: 'start' | 'center';
};

/**
 * Full DialogTitle props combining own props with HTML element props (including ref)
 */
export type DialogTitleProps<
  T extends AllowedDialogTitleElements = typeof DEFAULT_ELEMENT,
> = DialogTitleOwnProps<T> &
  Omit<ComponentPropsWithRef<T>, keyof DialogTitleOwnProps<T>>;

export const dialogTitleVariants = cva(s.title, {
  variants: {
    position: {
      start: s.startVariant,
      center: s.centerVariant,
    },
  },
});

export const DialogTitle = <
  T extends AllowedDialogTitleElements = typeof DEFAULT_ELEMENT,
>(
  props: DialogTitleProps<T>,
) => {
  const {
    as = DEFAULT_ELEMENT,
    asChild,
    children,
    className,
    position = 'start',
    ref,
    ...attrs
  } = props;
  const { id } = useDialog();

  const sharedProps = {
    id: `${id}-title`,
    className: clsx(dialogTitleVariants({ position }), className),
    ...attrs,
    children: <span>{children}</span>,
  };

  if (asChild) {
    return <Slot ref={ref} {...sharedProps} />;
  }

  const { Component, props: componentProps } = getPolymorphicProps(as, {
    ref,
    ...sharedProps,
  });
  return <Component {...componentProps} />;
};
